import {
  getTheme,
  IBreadcrumbProps,
  IStyle,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  Shimmer,
  Stack
} from "@fluentui/react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Card, Divider } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import DisplayAPIsException from "../BasicComponents/DisplayAPIsExceptionAlert";
import PartnerContact from "../components/PartnerView/PartnerContact";
import PartnerDetails from "../components/PartnerView/PartnerDetails";
import PartnerInfo from "../components/PartnerView/PartnerInfo";
import { PartnerInfoIdentity } from "../components/PartnerView/PartnerInfoIdentity";
import { PartnerPivot } from "../components/PartnerView/PartnerPivot";
import { logPageView } from "../helper/AppInsightsHelper";
import { IPartner } from "../interfaces/PartnerView.interfaces";
import { CustomBreadcrumb } from "../SharedComponents/CustomBreadcrumb";
import ShareLinkContextMenu from "../SharedComponents/ShareLinkContextMenu";
import {
  useGetPartnerInformationDataMutation,
  useLazyGetPartnersAIInsightDataQuery
} from "../slices/ExplorePartners.slice";

const bgColor = "#e6faff";
const partnerInfoDetailsColor = "#1a1b19";
const commonColor: string = "hsl(206, 100%, 42%)";
const styles = mergeStyleSets({
  partnerInfoContainer: { margin: 10, minWidth: 600 } as IStyle,
  partnerInfoIdentity: {
    marginLeft: 10,
    fontSize: 14,
    paddingBottom: 10,
    display: "flex",
    justifyContent: "space-between",
    color: partnerInfoDetailsColor
  } as IStyle,
  partnerInfoDetails: {
    display: "flex",
    color: partnerInfoDetailsColor
  } as IStyle,
  partnerDetails: {
    border: "solid 0.7px #ececec",
    marginBottom: "10px",
    borderRadius: 4,
    boxShadow: "-1px 2px 1px -1.5px rgba(0,0,0,0.2)"
  } as IStyle,
  partnerInfoIdentityInitialLetterStack: {
    fontSize: 44,
    color: commonColor,
    paddingTop: 8,
    border: "0.7px",
    borderRadius: 10
  } as IStyle,
  partnerInfoIdentityInitialLetterStackLabel: {
    background: bgColor
  } as IStyle,
  partnerInfoLabel: {
    fontWeight: "600",
    fontSize: "16px"
  } as IStyle,
  infoSpace: {
    paddingBottom: "8px"
  } as IStyle
});

export default function PartnerPage() {
  const [params] = useSearchParams();
  const partnerOneID = params.get("partnerOneId");
  const mpnId = params.get("mpnId");
  const [partner, setPartner] = useState<IPartner>({} as IPartner);
  const navigate = useNavigate();
  const [hideInfo, setHideInfo] = useState(false);
  const partnerDetailsRef = useRef<any>();
  const [getPartnerInformationData, { data, error, isLoading }] = useGetPartnerInformationDataMutation();
  const [
    triggerAIInsightDataQuery,
    { data: insightsSummary, isSuccess: insightsSummaryLoaded, isError: insightsSummaryError }
  ] = useLazyGetPartnersAIInsightDataQuery();
  const [partnersList, setPartnersList] = useState<any[]>([]);

  // Call the mutation when needed, e.g., inside a useEffect or on a button click
  useEffect(() => {
    if (partnerOneID) {
      getPartnerInformationData({
        partnerOneID: partnerOneID,
        mpnIdentifier: mpnId ?? undefined
      });
    }
  }, [partnerOneID, mpnId]);

  useEffect(() => {
    if (!params.get("partnerOneId")?.trim()) {
      navigate("/404");
      return;
    }

    if (data) {
      setPartner(
        (prev) =>
          ({
            ...prev,
            data: data?.partnerInformation,
            name: data?.partnerInformation?.partnerOneName ?? "",
            partnerOneID: data?.partnerInformation?.partnerOneID ?? "",
            mpnID: data?.partnerInformation?.mpnId ?? "",
            mpnName: data?.partnerInformation?.mpnName ?? ""
          }) as IPartner
      );
      setPartnersList(
        Object.values(data?.partnersList ?? {}).map(({ key, value }) => ({
          label: `${value} - ${key}`,
          value: key
        }))
      );
    }

    const customPropsForLogging = {
      PartnerName: partner?.name,
      PartnerOneId: partnerOneID
    };

    logPageView("Partner Page", window.location.href, customPropsForLogging);
  }, [navigate, params, data]);

  const hideMe = (value: any) => {
    setHideInfo(value);
  };

  useEffect(() => {
    if (partner.name) {
      triggerAIInsightDataQuery(
        {
          componentName: "partnerDescription",
          partnerOneID: partnerOneID ?? undefined,
          partnerOneName: partner.name
        },
        true
      );
    }
  }, [partner.name]);

  const handlePartnerChange = (selectedOption: any) => {
    if (selectedOption.label !== undefined) {
      navigate(
        "/partners?partnerOneId=" +
        encodeURIComponent(partnerOneID ?? "") +
        "&mpnId=" +
        encodeURIComponent(selectedOption.value)
      );
    }
  };

  if (!isLoading && error) {
    return (
      <>
        <HeaderSection name={partner.name} />
        <div className={styles.partnerInfoContainer}>
          <Card sx={{ mb: 2 }}>
            <DisplayAPIsException error={error} height={300} />
          </Card>
          <Card>
            <PartnerPivot partnerOneName={partner.name} partnerOneID={partner.partnerOneID} mpnId={partner.mpnID} />
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <HeaderSection name={partner.name} />
      <div className={styles.partnerInfoContainer}>
        <div className={styles.partnerInfoIdentity}>
          <PartnerInfoIdentity
            loading={isLoading}
            name={partner.name}
            partnerOneID={partnerOneID}
            mpnId={partner.mpnID}
            mpnName={partner.mpnName}
            error={error}
          />
          {isLoading ? (
            <div style={{ width: 350, height: 40, margin: "10px 0" }}>
              <Stack horizontal verticalAlign="center">
                <Shimmer width={100} height={40} style={{ marginRight: 10 }} />
                <Shimmer width={220} height={40} />
              </Stack>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}
            >
              <label>View By Partner:</label>
              <div style={{ width: 300 }}>
                <Select
                  defaultValue={{
                    label: partner.mpnName,
                    value: partner.mpnID
                  }}
                  value={{ label: `${partner.mpnName} - ${partner.mpnID}`, value: partner.mpnID }}
                  onChange={handlePartnerChange}
                  options={partnersList}
                  isSearchable={true}
                  menuPortalTarget={document.body}
                  aria-label="Partner Name Search"
                />
              </div>
            </div>
          )}
        </div>
        <br />
        <div className={styles.partnerDetails} ref={partnerDetailsRef}>
          <div className={styles.infoSpace}>
            {hideInfo ? (
              <KeyboardArrowDownIcon
                titleAccess="Click To Show Partner Information"
                sx={{
                  color: commonColor,
                  fontSize: "2rem"
                }}
                style={{
                  cursor: "pointer",
                  marginBottom: "-10px"
                }}
                onClick={() => hideMe(false)}
              />
            ) : (
              <KeyboardArrowUpIcon
                titleAccess="Click To Hide Partner Information"
                sx={{
                  color: commonColor,
                  fontSize: "2rem"
                }}
                style={{
                  cursor: "pointer",
                  marginBottom: "-10px"
                }}
                onClick={() => hideMe(true)}
              />
            )}
            <span className={styles.partnerInfoLabel}> Partner Information</span>
          </div>

          {!hideInfo
            ? (!error && (
              <div className={styles.partnerInfoDetails}>
                <Stack horizontal styles={{ root: { flex: 1, minHeight: 168 } }}>
                  <Stack horizontal styles={{ root: { width: "calc(40% + 0.8px)" } }}>
                    <PartnerInfo
                      name={partner.name}
                      loaded={insightsSummaryLoaded}
                      error={insightsSummaryError}
                      displaytext={insightsSummary}
                    />
                    <Divider orientation="vertical" style={{ marginBottom: "20px" }} flexItem />
                  </Stack>
                  <Stack horizontal styles={{ root: { flex: 1 } }}>
                    <PartnerDetails Details={partner.data} loading={isLoading} />
                    <Divider orientation="vertical" flexItem style={{ marginBottom: "20px" }} />
                    <PartnerContact loading={isLoading} partnerOneName={partner.name} />
                  </Stack>
                </Stack>
              </div>
            )) ||
            (!!error && <DisplayAPIsException height={Math.max(partnerDetailsRef.current?.offsetHeight, 200)} />)
            : null}
        </div>
        <Card>
          <PartnerPivot partnerOneName={partner.name} partnerOneID={partner.partnerOneID} mpnId={partner.mpnID} />
        </Card>
      </div>
    </>
  );
}

const HeaderSection = (props: { name: string }) => {
  const theme = getTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Check if URL contains 'mf' or 'tcf' parameters
  const hasFilters = searchParams.has("mf") || searchParams.has("tcf");
  const styles = mergeStyleSets({
    partnerHeaderContainer: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between"
    } as IStyle,
    partnerHeaderContainerItem: { flexGrow: 1 } as IStyle
  });
  const bcrops: IBreadcrumbProps = {
    items: [
      { text: "Home", key: "home", onClick: () => navigate("/") },
      { text: "Explore Partners", key: "explore-partners", onClick: () => navigate("/partnersearch") },
      { text: props.name, key: "partner-name", isCurrentItem: true }
    ],
    styles: breadcrumbStyles
  };

  return (
    <div className={styles.partnerHeaderContainer}>
      <div className={styles.partnerHeaderContainerItem}>
        <CustomBreadcrumb {...bcrops} key={key} />
      </div>
      <Stack
        horizontal
        styles={{
          root: { justifyContent: "space-between", alignItems: "center", columnGap: "10px", paddingRight: 10 }
        }}
      >
        {hasFilters && (
          <MessageBar
            messageBarType={MessageBarType.warning}
            messageBarIconProps={{ iconName: "Filter" }}
            styles={{
              root: { borderRadius: 4, border: `1px solid ${theme.palette.orange}` },
              icon: { color: theme.palette.orange },
              text: { color: "#663c00" }
            }}
          >
            Filters are applied on this page!
          </MessageBar>
        )}
        <ShareLinkContextMenu pageType="Partner" entityName={props.name} />
      </Stack>
    </div>
  );
};

const key = "partner-page-bc";

const breadcrumbStyles = {
  root: {
    fontSize: "14px",
    marginTop: 10,
    "--itemLink-color": "rgb(0, 120, 212)"
  },
  item: {
    fontSize: "inherit"
  },
  itemLink: {
    fontSize: "inherit",
    color: "var(--itemLink-color)",
    ":hover": {
      background: "none",
      textDecoration: "underline",
      color: "var(--itemLink-color)"
    }
  }
};
